import { useEffect } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { apiClient } from "@lib/apiClient";

interface AriaVoipPlugin {
  register(options: { accessToken: string }): Promise<void>;
}

const AriaVoip = registerPlugin<AriaVoipPlugin>("AriaVoip");

/**
 * Native iOS VoIP registration. Hands a Twilio access token to the native
 * layer, which binds it to the PushKit device token so incoming calls ring
 * through CallKit even when the app is closed or the phone is locked.
 * No-op on the web and on app builds that predate the native plugin.
 */
export function useVoipRegistration(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return;
    let cancelled = false;

    void (async () => {
      try {
        const { token } = await apiClient.get<{ token: string }>("/voice/token");
        if (cancelled || !token) return;
        await AriaVoip.register({ accessToken: token });
      } catch {
        // Plugin missing (older build) or token fetch failed — non-fatal; the
        // in-app JS registration still rings while the app is open.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
